/**
 * EmployeeDetailHeader — shared presentational header for employee detail views.
 *
 * Used by:
 *  - AdminEmployeeDetailPage (testids: admin-employee-*)
 *  - EmployeePortalPage (testids: portal-employee-*)
 *
 * Prototype reference: web/Clock Web.html lines 801-815 (EmployeePortal header)
 * and AdminEmployeeDetailPage lines 130-176.
 *
 * Pure presentational — no hooks, no domain imports.
 * Callers supply all data and i18n strings as props.
 *
 * Slice C changes:
 *  - Removed jefeLabel / jefeTestId props + Jefe line
 *  - Replaced combined cargoDeptTestId slot with two conditional lines:
 *    Cargo (cargoTestId) + Departamento (deptTestId)
 *  - capitalizeIfUpper applied to cargo and dept before display
 */
import type { ThemeTokens, BrandColors } from '@/shared/ui/theme/tokens';
import { capitalizeIfUpper } from '@/shared/utils/mapping';
import { Avatar } from '@/presentation/components/Avatar';
import { BackButton } from '@/presentation/components/BackButton';

interface EmployeeDetailHeaderProps {
  /** Employee initials for the Avatar fallback (e.g. "AP"). */
  initials: string;
  /** Avatar background color hex string. */
  avatarColor: string;
  /** Photo URL or null (Avatar handles load/error). */
  photoUrl: string | null;
  /** Employee full display name. */
  fullName: string;
  /** Position / job title. null → Cargo line omitted. */
  cargo?: string | null;
  /** Department. null → Departamento line omitted. */
  dept?: string | null;
  /** Navigate-back handler. */
  onBack: () => void;
  /** Centered page title (e.g. "Mis Marcajes" / "Detalle de empleado"). */
  title: string;
  // ── data-testid props (caller supplies distinct DOM contracts) ──────────────
  headerTestId: string;
  titleTestId: string;
  /** testid for the conditional Cargo: line (omitted when cargo is null/empty). */
  cargoTestId: string;
  /** testid for the conditional Departamento: line (omitted when dept is null/empty). */
  deptTestId: string;
  // ── Theme ──────────────────────────────────────────────────────────────────
  T: ThemeTokens;
  C: BrandColors;
}

export function EmployeeDetailHeader({
  initials,
  avatarColor,
  photoUrl,
  fullName,
  cargo,
  dept,
  onBack,
  title,
  headerTestId,
  titleTestId,
  cargoTestId,
  deptTestId,
  T,
  C: _C,
}: EmployeeDetailHeaderProps) {
  return (
    <div
      data-testid={headerTestId}
      style={{
        padding: '24px 30px 18px',
        borderBottom: `1px solid ${T.border}`,
        background: T.surfaceBg,
        flexShrink: 0,
      }}
    >
      <div style={{ maxWidth: 645, margin: '0 auto', width: '100%' }}>
        {/* BackButton + centered title row (prototype lines 801-803) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 12,
            position: 'relative',
          }}
        >
          <BackButton T={T} onClick={onBack} />
          <div
            data-testid={titleTestId}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 700,
              color: T.clockText,
              pointerEvents: 'none',
            }}
          >
            {title}
          </div>
        </div>

        {/* Centered avatar + identity block (prototype lines 804-815) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 12,
          }}
        >
          <Avatar
            initials={initials}
            color={avatarColor}
            size={72}
            photoUrl={photoUrl}
          />
          <div>
            <div style={{ fontSize: 19, fontWeight: 700, color: T.clockText }}>
              {fullName}
            </div>
            {cargo && (
              <p
                data-testid={cargoTestId}
                style={{ fontSize: 13, color: T.textBody, marginTop: 4, marginBottom: 0 }}
              >
                Cargo: {capitalizeIfUpper(cargo)}
              </p>
            )}
            {dept && (
              <p
                data-testid={deptTestId}
                style={{ fontSize: 13, color: T.textBody, marginTop: 3, marginBottom: 0 }}
              >
                Departamento: {capitalizeIfUpper(dept)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
